import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

// Lightweight Google Identity integration for CRA
// Exposes: AuthProvider, useAuth to access token and user info

type JwtPayload = {
  iss?: string;
  azp?: string;
  aud?: string;
  sub?: string;
  email?: string;
  email_verified?: boolean | string;
  name?: string;
  picture?: string;
  exp?: number | string;
  iat?: number | string;
};

export type AuthUser = {
  email?: string;
  name?: string;
  picture?: string;
  sub?: string;
};

type AuthContextValue = {
  ready: boolean;
  isLoggedIn: boolean;
  token: string | null;
  user: AuthUser | null;
  clientId?: string;
  adminEmails: string[];
  signOut: () => void;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function decodeBase64Url(input: string): string {
  try {
    const base64 = input.replace(/-/g, "+").replace(/_/g, "/");
    const pad = base64.length % 4;
    const padded = pad ? base64 + "=".repeat(4 - pad) : base64;
    // atob is available in browsers (CRA)
    return decodeURIComponent(
      Array.prototype.map
        .call(atob(padded), (c: string) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
  } catch {
    return "{}";
  }
}

function decodeJwt(token: string): JwtPayload | null {
  try {
    const parts = token.split(".");
    if (parts.length < 2) return null;
    const json = decodeBase64Url(parts[1]);
    const obj = JSON.parse(json);
    return obj || null;
  } catch {
    return null;
  }
}

function parseCsv(input?: string): string[] {
  if (!input) return [];
  return input
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

declare global {
  interface Window {
    google?: any;
  }
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const clientId = (process.env.REACT_APP_GOOGLE_CLIENT_ID || "").trim() || undefined;
  const adminEmails = useMemo(() => parseCsv(process.env.REACT_APP_ADMIN_EMAILS), []);

  const [ready, setReady] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [user, setUser] = useState<AuthUser | null>(null);

  // load any saved session
  useEffect(() => {
    try {
      const saved = localStorage.getItem("g_id_token");
      if (saved) {
        setToken(saved);
        const payload = decodeJwt(saved);
        if (payload) setUser({ email: payload.email, name: payload.name, picture: payload.picture, sub: payload.sub });
      }
    } catch {}
  }, []);

  // Load the Google Identity Services script and initialize
  useEffect(() => {
    if (!clientId) {
      setReady(true); // no client configured; keep UI functional without auth
      return;
    }
    let cancelled = false;
    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (cancelled) return;
      try {
        window.google?.accounts.id.initialize({
          client_id: clientId,
          callback: (resp: { credential?: string }) => {
            const idt = resp?.credential;
            if (!idt) return;
            setToken(idt);
            try { localStorage.setItem("g_id_token", idt); } catch {}
            const payload = decodeJwt(idt);
            setUser(payload ? { email: payload.email, name: payload.name, picture: payload.picture, sub: payload.sub } : null);
          },
          auto_select: true,
          ux_mode: "popup",
          itp_support: true,
        });
      } catch {}
      setReady(true);
    };
    document.head.appendChild(script);
    return () => {
      cancelled = true;
      try { document.head.removeChild(script); } catch {}
    };
  }, [clientId]);

  const signOut = useCallback(() => {
    try { window.google?.accounts.id.disableAutoSelect?.(); } catch {}
    setToken(null);
    setUser(null);
    try { localStorage.removeItem("g_id_token"); } catch {}
  }, []);

  const value: AuthContextValue = {
    ready,
    isLoggedIn: !!token,
    token,
    user,
    clientId,
    adminEmails,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
